const endpointErrorLogs = [];
const subscribers = new Set();

/**
 * Best-effort extraction of the server's own error message from a failed
 * response (e.g. MindBody's {"Message":"Staff identity authentication failed."}).
 * Reads a clone so the caller can still consume the body. ALWAYS returns a
 * string: some endpoints nest the error object (e.g. { Error: { Message } }),
 * and returning an object here crashes React when the log table renders it.
 */
export async function extractServerMessage(response) {
  try {
    const text = await response.clone().text();
    if (!text) return "";
    let candidate = "";
    try {
      const data = JSON.parse(text);
      candidate =
        data.Message ?? data.message ?? data.error ?? data.Error ?? "";
      if (candidate && typeof candidate === "object") {
        candidate =
          candidate.Message ||
          candidate.message ||
          JSON.stringify(candidate);
      }
      if (!candidate) candidate = text;
    } catch {
      candidate = text;
    }
    return String(candidate).slice(0, 200);
  } catch {
    return "";
  }
}

function notifySubscribers() {
  const snapshot = [...endpointErrorLogs];
  subscribers.forEach((callback) => callback(snapshot));
}

export function addEndpointErrorLog(logData) {
  endpointErrorLogs.unshift({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    ...logData,
  });

  // Keep logs bounded to avoid uncontrolled growth.
  if (endpointErrorLogs.length > 200) {
    endpointErrorLogs.length = 200;
  }

  notifySubscribers();
}

export function getEndpointErrorLogs() {
  return [...endpointErrorLogs];
}

export function clearEndpointErrorLogs() {
  endpointErrorLogs.length = 0;
  notifySubscribers();
}

export function subscribeEndpointErrorLogs(callback) {
  subscribers.add(callback);
  callback(getEndpointErrorLogs());
  return () => {
    subscribers.delete(callback);
  };
}

export function installFetchErrorLogger() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  if (window.__lbFetchErrorLoggerInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (url, options = {}) => {
    const method = options.method || "GET";
    const endpoint = typeof url === "string" ? url : url?.url || "unknown-url";
    const skipErrorLog = options.__skipErrorLog === true;

    try {
      const response = await originalFetch(url, options);

      if (!response.ok && !skipErrorLog) {
        const serverMessage = await extractServerMessage(response);
        addEndpointErrorLog({
          source: "fetch",
          method,
          endpoint,
          status: response.status,
          message: serverMessage || response.statusText || "Request failed",
        });
      }

      return response;
    } catch (error) {
      if (!skipErrorLog) {
        addEndpointErrorLog({
          source: "fetch",
          method,
          endpoint,
          status: "NETWORK_ERROR",
          message: error?.message || "Network request failed",
        });
      }
      throw error;
    }
  };

  window.__lbFetchErrorLoggerInstalled = true;
}
